import { useId, type ReactNode } from 'react';
import './interruptor.css';

export type InterruptorProps = {
  ligado: boolean;
  aoAlternar: () => void;
  /** O que ele liga: "Cards na tela". */
  rotulo: string;
  /** O efeito, numa frase, embaixo do rótulo — e o que o leitor de tela lê junto. */
  descricao?: ReactNode;
  /** Desabilitado exige motivo, como no botão: ele entra na descrição. */
  desabilitado?: boolean;
  motivo?: string;
  /**
   * De QUAL coisa é o interruptor, quando há vários iguais na tela: numa lista
   * de automações, todos dizem "Ligada". Entra no nome que o leitor de tela lê,
   * depois do rótulo visível ("Ligada, Menu de atendimento").
   */
  contexto?: string;
};

/**
 * Liga e desliga UMA coisa, com efeito imediato.
 *
 * ── Por que não a pílula de filtro ───────────────────────────────────────
 *
 * A pílula RECORTA o que a tela mostra; o interruptor muda o que o sistema
 * FAZ. Já houve um "interruptor de desligar com cara de filtro" neste produto
 * (docs/design/lacunas-do-produto.md), e quem o usou achou que estreitava uma
 * regra quando desligava a regra inteira. A forma diferente é o aviso.
 *
 * ── `role="switch"` ──────────────────────────────────────────────────────
 *
 * O leitor de tela diz "ligado"/"desligado", e não "pressionado", que é o que
 * `aria-pressed` diria — e "pressionado" não descreve uma preferência.
 */
export function Interruptor({
  ligado,
  aoAlternar,
  rotulo,
  descricao,
  desabilitado = false,
  motivo,
  contexto,
}: InterruptorProps) {
  const id = useId();
  const explicacao = desabilitado && motivo ? motivo : descricao;

  return (
    <div className="interruptor" data-desabilitado={desabilitado ? 'sim' : undefined}>
      <button
        type="button"
        role="switch"
        className="interruptor__botao"
        aria-checked={ligado}
        aria-label={contexto ? `${rotulo}, ${contexto}` : undefined}
        aria-describedby={explicacao ? `${id}-descricao` : undefined}
        disabled={desabilitado}
        onClick={aoAlternar}
      >
        <span className="interruptor__trilho" aria-hidden="true">
          <span className="interruptor__alavanca" />
        </span>
        <span className="interruptor__rotulo">{rotulo}</span>
      </button>
      {explicacao && (
        <p id={`${id}-descricao`} className="interruptor__descricao">
          {explicacao}
        </p>
      )}
    </div>
  );
}
